import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { useSnackbar } from '../contexts/SnackbarProvider';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Table, TableHead, TableBody, TableRow, TableCell,
    TextField, MenuItem, Checkbox, Typography, Box, Alert, Chip
} from "@mui/material";

const AllocateByWarehouseModal = ({ open, onClose, orderId, warehouseId, refetch }) => {
    const { showSnackbar } = useSnackbar();
    const [items, setItems] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
    const [splitItems, setSplitItems] = useState({});

    useEffect(() => {
        if (!open) return;

        const fetchData = async () => {
            try {
                setSelectedWarehouseId(warehouseId || "");
                setSplitItems({});

                const res = await axiosInstance.get(
                    `/order/${orderId}/allocation-candidates`
                );

                setItems(res.data.items);

                const map = new Map();
                res.data.items.forEach(item => {
                    item.warehouses.forEach(w => {
                        map.set(w.warehouse_id, w.warehouse_name);
                    });
                });

                setWarehouses(
                    Array.from(map.entries()).map(([id, name]) => ({
                        warehouse_id: id,
                        warehouse_name: name
                    }))
                );

            } catch (err) {
                if (process.env.NODE_ENV === 'development') {
                    console.error(err);
                }
                showSnackbar(
                    err?.response?.data?.message || 'データ取得に失敗しました',
                    'error'
                );
            }
        };

        fetchData();

    }, [open, orderId, warehouseId]);


    const handleSplitOrder = async () => {
        const splitIds = Object.keys(splitItems).filter(id => splitItems[id]);
        if (splitIds.length === 0) return;

        // 全チェック禁止
        if (splitIds.length === items.length) {
            showSnackbar('すべての注文アイテムが選択されているため、分割できません。', 'error');
            return;
        }

        try {
            await axiosInstance.post(`/order/${orderId}/split`, {
                split_item_ids: splitIds
            });
            onClose();
            refetch();

        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error(err);
            }
            showSnackbar(err?.response?.data?.message || '分割に失敗しました', 'error');
        }
    };

    const handleConfirmAllocate = async () => {
        try {
            await axiosInstance.post(`/order/${orderId}/confirm-warehouse`, {
                warehouse_id: selectedWarehouseId
            });

            onClose();
            refetch();
        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error(err);
            }
            showSnackbar(err?.response?.data?.message || '登録に失敗しました', 'error');

        }
    };
    const handleUnallocate = async () => {
        if (!window.confirm('この注文の在庫引当をすべて解除しますか？')) {
            return;
        }

        try {
            await axiosInstance.post(
                `/order/${orderId}/unallocate`
            );

            // 再取得して画面更新
            const res = await axiosInstance.get(
                `/order/${orderId}/allocation-candidates`
            );

            setItems(res.data.items);
            refetch();

        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error(err);
            }
            showSnackbar(err?.response?.data?.message || '在庫解除に失敗しました', 'error');
        }
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWith>
            <DialogTitle>有効在庫確認・倉庫割当</DialogTitle>

            <DialogContent dividers>
                <Box>
                    {
                        items.some(item => !item.variant_id) && (
                            <Alert severity="error">
                                紐づいていない商品があるため、在庫を確定できません。
                                画面を閉じて、商品の紐づけを行ってください。
                            </Alert>
                        )
                    }
                    {warehouseId && (
                        <Alert severity="success" sx={{ py: 0.5 }}>
                            この注文はすでに在庫引当済みです
                        </Alert>
                    )}
                    <TextField
                        select
                        label="出荷倉庫"
                        SelectProps={{ displayEmpty: true }}
                        value={selectedWarehouseId}
                        disabled={!!warehouseId}
                        onChange={(e) => {
                            setSelectedWarehouseId(Number(e.target.value));
                            setSplitItems({});
                        }}
                        sx={{ width: 240, my: 3 }}
                    >
                        <MenuItem value="">
                            倉庫を選択してください
                        </MenuItem>

                        {warehouses.map(w => (
                            <MenuItem key={w.warehouse_id} value={w.warehouse_id}>
                                {w.warehouse_name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {!warehouseId && (<TableCell>分割</TableCell>)}
                            <TableCell>商品</TableCell>
                            <TableCell sx={{ width: 120, minWidth: 120 }}>注文数</TableCell>
                            {warehouses.map(w => (
                                <TableCell key={w.warehouse_id}>
                                    {w.warehouse_name}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {items.map(item => (
                            <TableRow key={item.order_item_id}>
                                {!warehouseId && (
                                    <TableCell>
                                        <Checkbox
                                            checked={!!splitItems[item.order_item_id]}
                                            onChange={(e) =>
                                                setSplitItems(prev => ({
                                                    ...prev,
                                                    [item.order_item_id]: e.target.checked
                                                }))
                                            }
                                        />
                                    </TableCell>
                                )}
                                <TableCell>
                                    <Typography sx={{ width: 300 }}>{item.product_name}</Typography>
                                    {item.components.map(c => (
                                        <Typography key={c.item_id} variant="caption" sx={{ display: "block", fontSize: 11 }}>
                                            {c.item_code} {c.item_name} （{c.required_quantity}）
                                        </Typography>
                                    ))}
                                    {
                                        !item.variant_id && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                sx={{
                                                    display: 'block',
                                                    fontWeight: 700,
                                                }}
                                            >
                                                商品データと紐づいていません
                                            </Typography>
                                        )
                                    }
                                </TableCell>
                                <TableCell sx={{ width: 150 }}>{item.quantity}</TableCell>
                                {warehouses.map(w => {
                                    const wh = item.warehouses.find(whItem => whItem.warehouse_id === w.warehouse_id);

                                    const complete = wh?.complete_stock ?? 0;
                                    const component = wh?.component_stock ?? 0;

                                    return (
                                        <TableCell key={w.warehouse_id}>
                                            <Chip label={`有効 ${complete}個`} size="small" sx={{ mb: 1 }} /><br />
                                            <Typography variant="caption" color='body2'>総 {wh.logical_stock}</Typography>
                                            {/*<Chip label={`作成可能 ${component}`} size="small" />*/}
                                        </TableCell>
                                    );
                                })}
                                <TableCell>
                                    {item.is_allocated && (
                                        <Chip
                                            size="small"
                                            label="引当済み"
                                            color="success"
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>

            <DialogActions>
                {/* 引当解除 */}
                <Button
                    color="error"
                    variant="outlined"
                    disabled={!warehouseId}
                    onClick={handleUnallocate}
                >
                    引当解除
                </Button>
                <Button
                    variant="outlined"
                    disabled={
                        !!warehouseId ||
                        Object.values(splitItems).filter(Boolean).length === 0
                    }
                    onClick={handleSplitOrder}
                >
                    注文書を分ける
                </Button>
                <Button
                    variant="contained"
                    disabled={
                        !!warehouseId ||
                        !selectedWarehouseId ||
                        items.some(item => !item.variant_id)
                    }
                    onClick={handleConfirmAllocate}
                >
                    この倉庫で在庫割当を確定
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AllocateByWarehouseModal;
